import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'

/**
 * Personalisation Page - Final step of onboarding (Step 3)
 * User provides feedback about how they heard about Reflow
 * Flow: Welcome → Setup → Personalisation
 */
export function PersonalisationPage() {
    const navigate = useNavigate()
    const [source, setSource] = useState('')

    const handleNext = () => {
        // TODO: Add business logic to save feedback
        navigate('/app')
    }

    return (
        <div className="h-screen flex flex-col md:flex-row overflow-hidden bg-white dark:bg-background">
            {/* Left side - Form */}
            <div className="w-full md:w-1/2 h-full flex flex-col justify-between p-8 md:p-16 lg:p-24 relative z-10">
                {/* Main Content */}
                <div className="max-w-md w-full mx-auto">
                    {/* Logo (Inside main container for alignment) */}
                    <div className="flex items-center gap-2 mb-8 group cursor-pointer hover:opacity-90 transition-opacity">
                        <img src="/ReflowAI Logo.svg" alt="Reflow" className="h-10 w-10 group-hover:scale-105 transition-transform" />
                        <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Reflow</span>
                    </div>

                    <h1 className="text-4xl md:text-5xl font-bold mb-8 text-gray-900 dark:text-white leading-tight">
                        How did you hear about Reflow?
                    </h1>

                    <form className="space-y-6">
                        <div>
                            <Label htmlFor="source" className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                                Tell us how you got here!
                            </Label>
                            <Textarea
                                id="source"
                                name="source"
                                placeholder="Googling 'Competitive Intelligence'..."
                                rows={4}
                                value={source}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSource(e.target.value)}
                                className="block w-full rounded-lg border-gray-300 dark:border-gray-600 shadow-sm focus:border-black focus:ring-black dark:focus:border-white dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white sm:text-sm resize-none p-4"
                            />
                        </div>

                        <div className="pt-4">
                            <Button
                                type="button"
                                onClick={handleNext}
                                className="bg-[#1A1A1A] hover:bg-black/90 dark:hover:bg-white/90 text-white dark:bg-white dark:text-black font-semibold py-3 px-8 rounded-md transition-colors duration-200 uppercase text-sm tracking-wide h-auto"
                            >
                                Next
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Progress Bar */}
                <div className="mt-12 w-full max-w-md mx-auto">
                    <div className="relative h-2 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-yellow-300 to-orange-400 rounded-full transition-all duration-500" />
                    </div>
                </div>
            </div>

            {/* Right side - Testimonials Panel */}
            <div className="hidden md:block w-1/2 bg-[#FFF4E6] dark:bg-[#2A2018] relative overflow-hidden transition-colors duration-200">
                {/* Background Image */}
                <div className="absolute bottom-0 right-0 w-full h-full flex items-end justify-center pointer-events-none">
                    <div className="w-full h-full bg-gradient-to-b from-transparent via-[#FFF4E6]/20 to-[#FFF4E6] dark:via-[#2A2018]/20 dark:to-[#2A2018] absolute inset-0 z-10" />
                    <img
                        alt="Abstract 3D golden shape"
                        className="w-[120%] h-auto object-cover opacity-90 dark:opacity-60 mix-blend-multiply dark:mix-blend-overlay transform translate-y-1/4 translate-x-10 filter blur-sm"
                        src="/onboarding/abstract-gold.jpg"
                    />
                </div>

                {/* Testimonial Cards */}
                <div className="relative z-10 w-full h-full p-12 flex flex-col justify-center items-center gap-6">
                    {/* Testimonial 1 */}
                    <TestimonialCard
                        name="Sarah Chen, Supply Chain Lead"
                        company="Logistics Innovators"
                        image="/onboarding/testimonial-sarah.jpg"
                        quote="Reflow's insights saved us weeks of manual tracking. Spotting the competitor's stock-out before it happened was a game changer for our Q4 strategy."
                        source="from Industry Weekly"
                        iconBg="bg-blue-100 dark:bg-blue-900"
                        iconColor="text-blue-600 dark:text-blue-300"
                        icon="inventory_2"
                        className="self-end transform -translate-x-12 translate-y-4"
                    />

                    {/* Testimonial 2 */}
                    <TestimonialCard
                        name="Marcus Rodriguez, Category Mgr"
                        company="QuickBasket Inc."
                        image="/onboarding/testimonial-marcus.jpg"
                        quote="I finally have visibility into real-time pricing across 5 regions. The automated alerts for price drops are pure gold."
                        source="from G2 Reviews"
                        iconBg="bg-yellow-100 dark:bg-yellow-900"
                        iconColor="text-yellow-600 dark:text-yellow-300"
                        icon="insights"
                        className="self-start transform translate-x-8 hover:scale-105 transition-transform duration-300"
                    />

                    {/* Testimonial 3 */}
                    <TestimonialCard
                        name="David Kim, Growth Director"
                        company="ScaleUp Commerce"
                        image="/onboarding/testimonial-david.jpg"
                        quote="The UI is incredibly intuitive. Onboarding my entire analyst team took less than an hour. Highly recommend for any competitive niche."
                        source="from LinkedIn"
                        iconBg="bg-purple-100 dark:bg-purple-900"
                        iconColor="text-purple-600 dark:text-purple-300"
                        icon="trending_up"
                        className="self-end transform -translate-x-4 -translate-y-4"
                    />
                </div>
            </div>
        </div>
    )
}

// Testimonial Card Component
interface TestimonialCardProps {
    name: string
    company: string
    image: string
    quote: string
    source: string
    iconBg: string
    iconColor: string
    icon: string
    className?: string
}

function TestimonialCard({ name, company, image, quote, source, iconBg, iconColor, icon, className = '' }: TestimonialCardProps) {
    return (
        <div className={`bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg max-w-sm border border-gray-100 dark:border-gray-700 transition-colors duration-200 ${className}`}>
            <div className="flex items-start gap-4 mb-3">
                <img alt={name} className="w-10 h-10 rounded-full object-cover" src={image} />
                <div>
                    <div className="font-semibold text-xs text-gray-900 dark:text-white">{name}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{company}</div>
                </div>
                <div className={`ml-auto ${iconBg} p-1 rounded-full`}>
                    <span className={`material-icons ${iconColor} text-xs`}>{icon}</span>
                </div>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                "{quote}"
            </p>
            <div className="mt-3 text-[10px] text-gray-400">{source}</div>
        </div>
    )
}
